import { Box, Button, FormikTextInput, Modal, toast, Toggle } from '@palmetto/palmetto-components';
import { Field, FieldArray, Form, Formik, FormikHelpers } from 'formik';
import { ChangeEvent, useCallback, useMemo } from 'react';
import * as yup from 'yup';

import { OrganizationMaterialClawbackSettingsV2, OrganizationMilestoneClawbackSettingsV2 } from 'types';

const buildClawbackSettingsSchema = (defaultValues: Record<string, number>, displayMap: Record<string, string>) => {
  const shape: Record<string, yup.NumberSchema> = {};
  Object.keys(defaultValues).forEach((key) => {
    shape[key] = yup
      .number()
      .min(14, `${displayMap[key] || key} days period must be at least 14 days`)
      .max(365, `${displayMap[key] || key} days period must be at most 365 days`)
      .required(`${displayMap[key] || key} days is required`);
  });

  return yup.object().shape({
    ...shape,
    inherit: yup.boolean(),
  });
};

interface ClawbackSettingsEditModalProps {
  isOpen: boolean;
  handleClose: () => void;
  handleSubmit: (values: any) => Promise<void>;
  currentClawbackSettings?: OrganizationMilestoneClawbackSettingsV2 | OrganizationMaterialClawbackSettingsV2;
  modalId: string;
  modalTitle: string;
  submitButtonText: string;
  isInherited: boolean;
  displayMap: Record<string, string>;
  defaultValues: Record<string, number>;
}

export const ClawbackSettingsEditModal = ({
  isOpen,
  handleClose,
  handleSubmit,
  currentClawbackSettings,
  modalId,
  modalTitle,
  submitButtonText = 'Save Changes',
  isInherited,
  displayMap,
  defaultValues,
}: ClawbackSettingsEditModalProps) => {
  const initialValues = useMemo(() => {
    return {
      ...defaultValues,
      ...currentClawbackSettings,
    };
  }, [currentClawbackSettings, defaultValues]);

  const showErrorToast = (message: string) => {
    toast.error(message, {
      style: {
        maxWidth: '500px',
        whiteSpace: 'pre-line',
        wordBreak: 'break-word',
      },
    });
  };

  const onSubmit = async (values: any, { setSubmitting, resetForm }: FormikHelpers<any>) => {
    try {
      await handleSubmit({ ...values });
      resetForm();
      handleClose();
      toast.success('Clawback settings updated successfully');
    } catch (error: any) {
      setSubmitting(false);
      showErrorToast(`Failed to update clawback settings: ${error.data.message}`);
    } finally {
      setSubmitting(false);
    }
  };

  const renderEventSchedule = useCallback(
    (isSubmitting: boolean, inherit: boolean) => {
      return (
        <Box childGap={{ base: 'lg', desktop: 'xl' }}>
          <Box direction="row" justifyContent="space-between" alignItems="center" gap="md">
            <Box width="xl">Rule Name</Box>
            <Box width="lg" textAlign={{ base: 'left', desktop: 'right' }}>
              Days
            </Box>
          </Box>

          {Object.entries(initialValues).map(([key, _value]) => (
            <Box key={key} direction="row" justifyContent="space-between" alignItems="center" gap="md">
              <Box width="xl">{displayMap[key] || key}</Box>
              <Box width="xl" textAlign={{ base: 'left', desktop: 'right' }}>
                <Field
                  type="number"
                  isDisabled={isSubmitting || inherit}
                  name={key}
                  id={key}
                  component={FormikTextInput}
                />
              </Box>
            </Box>
          ))}
        </Box>
      );
    },
    [initialValues, displayMap],
  );

  return (
    <Formik
      validationSchema={buildClawbackSettingsSchema(defaultValues, displayMap)}
      initialValues={{ ...initialValues, inherit: isInherited }}
      onSubmit={onSubmit}
      enableReinitialize
      validateOnBlur
    >
      {({ isSubmitting, resetForm, submitForm, values, setFieldValue }) => (
        <Form>
          <Modal
            isOpen={isOpen}
            maxWidth="4xl"
            ariaLabelledBy={modalId}
            onDismiss={() => {
              if (!isSubmitting) {
                resetForm();
                handleClose();
              }
            }}
          >
            <Modal.Header
              id={modalId}
              title={modalTitle}
              onDismiss={() => {
                if (!isSubmitting) {
                  resetForm();
                  handleClose();
                }
              }}
            />
            <Modal.Body background="secondary" childGap="lg">
              <Box childGap={{ base: 'lg', desktop: 'xl' }}>
                <Box borderWidth="xs" borderColor="grey-100" padding="md" radius="md">
                  <FieldArray
                    name="clawbackSchedule"
                    render={() => renderEventSchedule(isSubmitting, values.inherit)}
                  />
                </Box>
              </Box>
              <Toggle
                id="inherit"
                label="Inheriting from parent"
                isChecked={values.inherit}
                onChange={(e: ChangeEvent<HTMLInputElement>) => {
                  setFieldValue('inherit', e.target.checked);
                }}
                isDisabled={isSubmitting}
              />
            </Modal.Body>
            <Modal.Footer>
              <Button
                variant="secondary"
                tone="neutral"
                isLoading={isSubmitting}
                isDisabled={isSubmitting}
                onClick={() => {
                  resetForm();
                  handleClose();
                }}
              >
                Cancel
              </Button>
              <Button
                variant="primary"
                isLoading={isSubmitting}
                isDisabled={isSubmitting}
                type="submit"
                onClick={submitForm}
              >
                {submitButtonText}
              </Button>
            </Modal.Footer>
          </Modal>
        </Form>
      )}
    </Formik>
  );
};
